import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../services/api';
import toast from 'react-hot-toast';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userRoles, setUserRoles] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  const adminRole = process.env.REACT_APP_ADMIN_ROLE || 'admin';
  const superAdminRole = process.env.REACT_APP_SUPER_ADMIN_ROLE || 'super-admin';

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const storedUser = localStorage.getItem('user_info');
    if (token && storedUser) {
      try {
        const userInfo = JSON.parse(storedUser);
        setUser(userInfo);
        const roles = userInfo.realm_roles || [];
        setUserRoles(roles);
        setIsAdmin(roles.includes(adminRole));
        setIsSuperAdmin(roles.includes(superAdminRole));
        setIsAuthenticated(true);
      } catch (e) {
        localStorage.clear();
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      const response = await authAPI.login(email, password);
      const { access_token, refresh_token } = response.data;

      localStorage.setItem('access_token', access_token);
      if (refresh_token) localStorage.setItem('refresh_token', refresh_token);

      const meResponse = await authAPI.getCurrentUser();
      const userInfo = meResponse.data;

      const roles = userInfo.realm_roles || [];
      const userObj = {
        sub: userInfo.sub,
        username: userInfo.username || email,
        email: userInfo.email || email,
        name: userInfo.name || email,
        groups: userInfo.groups || [],
        realm_roles: roles,
        is_admin: roles.includes(adminRole),
        is_super_admin: roles.includes(superAdminRole),
      };
      localStorage.setItem('user_info', JSON.stringify(userObj));
      setUser(userObj);
      setUserRoles(roles);
      setIsAdmin(roles.includes(adminRole));
      setIsSuperAdmin(roles.includes(superAdminRole));
      setIsAuthenticated(true);

      toast.success(`Вітаємо, ${userObj.name || userObj.username}!`);
      return { success: true };
    } catch (error) {
      toast.error('Невірний email або пароль. Спробуйте ще раз.');
      return { success: false };
    }
  };

  // ✨ Новая функция register – только email, firstName, lastName
  const register = async (email, firstName, lastName) => {
    try {
      // Вызываем API без пароля
      const response = await authAPI.register(email, firstName, lastName);
      const data = response.data;

      if (data.status === 'pending_verification') {
        toast.success(data.message || 'Користувач з таким email вже існує, але не підтверджений. Лист надіслано повторно.');
        return { success: true, pendingVerification: true, message: data.message };
      }

      toast.success('Реєстрація успішна! Перевірте вашу пошту для підтвердження та встановлення пароля.');
      return { success: true, pendingVerification: false };
    } catch (error) {
      const status = error.response?.status;
      let message = 'Сталася помилка. Спробуйте пізніше.';
      if (status === 409) {
        message = 'Користувач з таким email вже існує. Спробуйте увійти або відновити пароль.';
      }
      toast.error(message);
      return { success: false, message };
    }
  };

  const logout = () => {
    authAPI.logout();
    setUser(null);
    setUserRoles([]);
    setIsAdmin(false);
    setIsSuperAdmin(false);
    setIsAuthenticated(false);
    toast.success('Ви вийшли з системи');
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, loading, login, register, logout, userRoles, isAdmin, isSuperAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};